import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { 
  ArrowLeft, CheckCircle, AlertCircle, CreditCard, Building2, 
  Copy, Check, FileText, Dog
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicePaymentPage = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [bankTransferSubmitted, setBankTransferSubmitted] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [copied, setCopied] = useState(null);

  // Check for Stripe success
  const stripeSessionId = searchParams.get('session_id');
  const paymentStatus = searchParams.get('status');

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/public/${invoiceId}`);
      setInvoice(res.data);
    } catch (err) {
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  // Check Stripe status if returning from checkout
  const checkStripeStatus = useCallback(async () => {
    if (stripeSessionId && paymentStatus === 'success') {
      setProcessing(true);
      try {
        const res = await axios.get(`${API}/invoices/public/${invoiceId}/stripe-status/${stripeSessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success("Payment successful! Thank you.");
          fetchInvoice(); // Refresh to show paid status
        }
      } catch (err) {
        console.error("Stripe status check failed:", err);
      } finally {
        setProcessing(false);
      }
    }
  }, [stripeSessionId, paymentStatus, invoiceId, fetchInvoice]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  useEffect(() => {
    if (stripeSessionId && invoice) {
      checkStripeStatus();
    }
  }, [stripeSessionId, invoice, checkStripeStatus]);

  const handleStripeCheckout = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/invoices/public/${invoiceId}/stripe-checkout`, {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setProcessing(false);
    }
  };

  const handlePayPalSuccess = async (orderId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/invoices/public/${invoiceId}/paypal-complete?paypal_order_id=${orderId}`);
      toast.success("Payment successful! Thank you.");
      fetchInvoice();
    } catch (err) {
      toast.error("Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleBankTransfer = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/invoices/public/${invoiceId}/bank-transfer`);
      setBankDetails(res.data.bank_details);
      setBankTransferSubmitted(true);
      toast.success("Bank transfer details sent to your email!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process request");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-olive border-t-transparent" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium text-center py-12"
          >
            <AlertCircle className="w-16 h-16 text-terracotta mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-charcoal mb-4">Invoice Not Found</h1>
            <p className="text-stone mb-6">
              This invoice may have been cancelled or the link is invalid.
            </p>
            <Link to="/">
              <Button className="btn-primary">Go to Homepage</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Already paid view
  if (invoice.status === "paid") {
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-charcoal text-white py-4">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </header>

        <div className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium text-center py-12"
          >
            <CheckCircle className="w-16 h-16 text-olive mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-charcoal mb-4">Payment Complete</h1>
            <p className="text-stone mb-2">Invoice #{invoice.invoice_number}</p>
            <p className="text-stone mb-6">
              Thank you for your payment of <span className="font-bold text-olive">${invoice.amount.toFixed(2)} AUD</span>
            </p>
            <p className="text-sm text-stone mb-6">A receipt has been sent to your email.</p>
            <Link to="/">
              <Button className="btn-primary">Go to Homepage</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Cancelled invoice
  if (invoice.status === "cancelled") {
    return (
      <div className="min-h-screen bg-cream">
        <div className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium text-center py-12"
          >
            <AlertCircle className="w-16 h-16 text-terracotta mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-charcoal mb-4">Invoice Cancelled</h1>
            <p className="text-stone mb-6">
              This invoice has been cancelled. Please contact us if you have questions.
            </p>
            <Link to="/">
              <Button className="btn-primary">Go to Homepage</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Bank transfer submitted view
  if (bankTransferSubmitted && bankDetails) {
    return (
      <div className="min-h-screen bg-cream">
        <header className="bg-charcoal text-white py-4">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium"
          >
            <div className="text-center mb-8">
              <Building2 className="w-16 h-16 text-olive mx-auto mb-4" />
              <h1 className="font-heading text-3xl text-charcoal mb-2">Bank Transfer Details</h1>
              <p className="text-stone">Complete your payment using the details below</p>
            </div>

            <div className="bg-sand/50 rounded-xl p-6 space-y-4">
              {[
                { label: "Account Name", value: bankDetails.account_name, key: "name" },
                { label: "BSB", value: bankDetails.bsb, key: "bsb", mono: true },
                { label: "Account Number", value: bankDetails.account_number, key: "account", mono: true },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone">{item.label}</p>
                    <p className={`font-medium text-charcoal ${item.mono ? 'font-mono' : ''}`}>{item.value}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(item.value, item.key)}
                    className="text-olive"
                  >
                    {copied === item.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              ))}
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Amount</p>
                  <p className="font-heading text-2xl text-olive">${bankDetails.amount.toFixed(2)} AUD</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-olive/10">
                <div>
                  <p className="text-sm text-stone">Reference (Important!)</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.reference}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.reference, 'reference')}
                  className="text-olive"
                >
                  {copied === 'reference' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-terracotta/10 rounded-lg">
              <p className="text-sm text-charcoal">
                <strong>Important:</strong> Please use the reference above so we can match your payment.
                We&apos;ll send you a receipt once payment is confirmed (usually 1-2 business days).
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-stone text-sm mb-4">A copy of these details has been sent to your email.</p>
              <Link to="/">
                <Button className="btn-primary">Return to Home</Button>
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  const invoiceTypeLabel = invoice.invoice_type === "single_day" ? "Single Day Training" : "4-Week Training Block";

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-charcoal text-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="font-heading text-4xl text-charcoal mb-2">Pay Invoice</h1>
          <p className="text-stone">Invoice #{invoice.invoice_number}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Invoice Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium"
          >
            <h2 className="font-heading text-2xl text-charcoal mb-6">Invoice Details</h2>
            
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-olive/10">
              <div className="w-16 h-16 rounded-full bg-olive/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-olive" />
              </div>
              <div>
                <h3 className="font-heading text-xl text-charcoal">{invoiceTypeLabel}</h3>
                <p className="text-stone">{invoice.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center">
                  <span className="text-olive text-sm font-medium">To</span>
                </div>
                <div>
                  <p className="text-sm text-stone">Bill To</p>
                  <p className="font-medium text-charcoal">{invoice.recipient_name}</p>
                </div>
              </div>
              
              {invoice.puppy_name && (
                <div className="flex items-center gap-3">
                  <Dog className="w-5 h-5 text-olive" />
                  <div>
                    <p className="text-sm text-stone">Puppy</p>
                    <p className="font-medium text-charcoal">{invoice.puppy_name}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-olive/10">
              <div className="flex justify-between items-center">
                <span className="text-lg text-charcoal">Amount Due</span>
                <span className="font-heading text-3xl text-olive">${invoice.amount.toFixed(2)} AUD</span>
              </div>
            </div>
          </motion.div>

          {/* Payment Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card-premium"
          >
            <h2 className="font-heading text-2xl text-charcoal mb-6">Select Payment Method</h2>

            {processing ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-olive border-t-transparent mx-auto mb-4" />
                <p className="text-stone">Processing...</p>
              </div>
            ) : !selectedMethod ? (
              <div className="space-y-3">
                {/* Stripe */}
                {invoice.stripe_publishable_key && (
                  <button
                    onClick={() => setSelectedMethod('stripe')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-stripe"
                  >
                    <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-olive" />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Credit / Debit Card</p>
                      <p className="text-sm text-stone">Pay securely with Stripe</p>
                    </div>
                  </button>
                )}

                {/* PayPal */}
                {invoice.paypal_available && (
                  <button
                    onClick={() => setSelectedMethod('paypal')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-paypal"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#003087]/10 flex items-center justify-center">
                      <span className="font-bold text-[#003087] text-sm">PP</span>
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">PayPal</p>
                      <p className="text-sm text-stone">Pay with your PayPal account</p>
                    </div>
                  </button>
                )}

                {/* Bank Transfer */}
                {invoice.bank_transfer_available && (
                  <button
                    onClick={() => setSelectedMethod('bank_transfer')}
                    className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                    data-testid="select-bank-transfer"
                  >
                    <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-olive" />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">Bank Transfer</p>
                      <p className="text-sm text-stone">Direct deposit to our account</p>
                    </div>
                  </button>
                )}
              </div>
            ) : selectedMethod === 'stripe' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <div className="text-center py-4">
                  <CreditCard className="w-12 h-12 text-olive mx-auto mb-4" />
                  <p className="text-charcoal mb-4">You&apos;ll be redirected to Stripe&apos;s secure checkout</p>
                  <Button
                    onClick={handleStripeCheckout}
                    className="btn-primary w-full"
                    data-testid="stripe-checkout-btn"
                  >
                    Pay ${invoice.amount.toFixed(2)} AUD
                  </Button>
                </div>
              </div>
            ) : selectedMethod === 'paypal' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <PayPalScriptProvider options={{ 
                  "client-id": invoice.paypal_client_id,
                  currency: "AUD"
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill" }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: {
                            value: invoice.amount.toFixed(2),
                            currency_code: "AUD"
                          },
                          description: `Invoice #${invoice.invoice_number} - ${invoiceTypeLabel}`
                        }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      const order = await actions.order.capture();
                      handlePayPalSuccess(order.id);
                    }}
                    onError={() => {
                      toast.error("Payment failed. Please try again.");
                    }}
                  />
                </PayPalScriptProvider>
              </div>
            ) : selectedMethod === 'bank_transfer' ? (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMethod(null)}
                  className="text-stone hover:text-charcoal"
                >
                  ← Back to payment options
                </Button>
                
                <div className="text-center py-4">
                  <Building2 className="w-12 h-12 text-olive mx-auto mb-4" />
                  <p className="text-charcoal mb-2">Bank Transfer</p>
                  <p className="text-sm text-stone mb-4">
                    We&apos;ll send you our bank details via email.
                  </p>
                  <Button
                    onClick={handleBankTransfer}
                    className="btn-primary w-full"
                    data-testid="bank-transfer-btn"
                  >
                    Get Bank Details
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="mt-6 pt-6 border-t border-olive/10">
              <div className="flex items-center gap-2 text-sm text-stone">
                <CheckCircle className="w-4 h-4 text-olive" />
                <span>Secure payment</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-stone text-sm">
            Questions? Contact us at{" "}
            <a href="mailto:thepackstandard@gmail.com" className="text-olive hover:underline">
              thepackstandard@gmail.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default InvoicePaymentPage;
